///
/// EarthBrowser
///
/// Created by Matt Giger on 10/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Refuses to enable a weather overlay whose textures would push the earth
/// tile shader past the fragment texture units WebGL2 guarantees (see
/// TextureUnitBudget). Without the gate the shader fails to link and every
/// tile disappears with nothing in the UI to explain it. Like the memory
/// gate, this sits at the middleware so every enable path is covered.
///

import { type Middleware } from "@reduxjs/toolkit"
import { toast } from "@/components/ui/use-toast"
import { datasetDisplayName } from "@/earthbrowser/utils/MemoryBudget"
import { canEnableDatasetTextureUnits, isOverlayDataset } from "@/earthbrowser/utils/TextureUnitBudget"
import { type DatasetType } from "@/types/DatasetElement"
import { track } from "@/utils/analytics"
import { sendDatasetAction } from "./earthBrowserSlice"

interface VisibleDatasetsState {
	earthbrowser?: { visibleDatasets?: DatasetType[] }
}

function visibleDatasets(state: unknown): DatasetType[] {
	return (state as VisibleDatasetsState).earthbrowser?.visibleDatasets ?? []
}

export const textureUnitGateMiddleware: Middleware = (api) => (next) => (action) => {
	if (sendDatasetAction.match(action) && action.payload.action === "enable") {
		const type = action.payload.type
		const visible = visibleDatasets(api.getState())
		const verdict = canEnableDatasetTextureUnits(type, visible)
		if (!verdict.allowed) {
			const name = datasetDisplayName(type)
			const others = visible.filter(other => other !== type && isOverlayDataset(other)).map(datasetDisplayName)
			track("layer_blocked_texture_units", { layer: type, units: verdict.units })
			console.warn(`[TextureUnitGate] Blocked enabling "${type}": ${verdict.units} fragment samplers > ${verdict.budget}`)
			toast({
				title: `Too many weather layers for ${name}`,
				description: `The globe can draw only so many weather layers at once. Turn off ${others.length > 0 ? others.join(", ") : "another weather layer"} first.`,
				duration: 6000,
			})
			// Swallow the action: the dataset stays disabled
			return action
		}
	}
	return next(action)
}
